<template>
  <div class="mobileViewStyle calendar-schedule">
    <!--
      新建 / 编辑日程（企业微信手机端）：日程是「某天的一件事」，只落一行，不拆分。
      ★ 带 id 进来就是编辑：同一套表单，saveSchedule 后端按 id 空不空决定新增还是更新
    -->
    <div class="detail-body">
      <list-card label="日程信息" type="form">
        <data-input label="日程类型">
          <mobile-select class="row-select" :clearable="false" v-model="form.calendarType"
                         placeholder="请选择日程类型" @change="onTypeChange">
            <mobile-option v-for="opt in scheduleTypes" :key="opt.value" :label="opt.label" :value="opt.value"/>
          </mobile-select>
        </data-input>
        <!--
          只有「拜访客户」才要选客户。
          ★ 取 partnerId（档案id）而不是 id（merchant 行主键）：日程的 partner_id 存的就是档案id，
            与 web 端 CustomerSelect、跟进记录的 partner_id 是同一个口径。
        -->
        <data-input v-if="Number(form.calendarType) === 4" label="拜访客户">
          <mobile-search-select v-model="form.partnerId" :query="queryCustomerPage"
                                label-column="merchantRemarkName" code-column="partnerId"
                                placeholder="请选择客户" search-placeholder="客户名称/手机号"
                                :display-text="partnerName" @change="partnerName = ''">
            <!-- 客户名称下方补一行联系电话：同名客户最容易记混，电话能区分 -->
            <template #item="{item}">
              <div class="mss-item">
                <div class="mss-item-main">{{ item.merchantRemarkName }}</div>
                <div class="mss-item-sub">{{ item.merchantRemarkPhone || "无联系电话" }}</div>
              </div>
            </template>
          </mobile-search-select>
        </data-input>
        <!-- 「商机客户」与拜访客户一样要挂到具体对象上，只是这里选的是商机 -->
        <data-input v-if="Number(form.calendarType) === 8" label="商机客户">
          <mobile-search-select v-model="form.partnerId" :query="queryOpportunityPage"
                                label-column="opportunityTitle" code-column="partnerId"
                                placeholder="请选择商机" search-placeholder="商机名称/编号"
                                :display-text="partnerName" @change="partnerName = ''"/>
        </data-input>
        <data-input label="日程标题" v-model="form.title" desc="" placeholder="请输入日程标题"/>
        <data-input label="日程日期">
          <mobile-date-select v-model="form.goalDate" placeholder="请选择日期"/>
        </data-input>
        <data-input label="备注" v-model="form.remark" desc="" placeholder="选填"/>
      </list-card>
    </div>

    <div class="detail-footer">
      <mobile-button @click="cancel">取消</mobile-button>
      <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileDateSelect from "@/components/qywx/data/MobileDateSelect.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import commonDataApi from "@/api/normal/common/commonDataApi";
import {dateOnly} from "@/views/h5/crm/kpi/kpiCalendarDictionary";
import {PARTNER_TYPE} from "@/views/h5/crm/crmDictionary";

/** 拜访客户：日程里要挂客户档案id的类型 */
const TYPE_VISIT = 4;
/** 商机客户：日程里要挂商机档案id的类型。跟进该商机时这条日程会自动置为已完成 */
const TYPE_OPPORTUNITY = 8;

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
function today() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "KCS",
  components: {
    MobileButton, MobileSearchSelect, MobileDateSelect, MobileOption, MobileSelect, DataInput, ListCard
  },
  data() {
    return {
      form: {
        id: "",
        employeeId: "",
        calendarType: 4,
        partnerId: "",
        title: "",
        goalDate: "",
        remark: "",
      },
      partnerName: "",
      // 日程类型，与后端 saveSchedule 的类型白名单一一对应
      scheduleTypes: [
        {value: 4, label: "拜访客户"},
        {value: 5, label: "线上会议"},
        {value: 6, label: "线下会议"},
        {value: 7, label: "产品学习"},
        {value: 8, label: "商机客户"},
        {value: 99, label: "其它"},
      ],
      pageTitle: "",
    };
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.isEdit ? "编辑日程" : "新建日程";
    // 员工与日期由日历页带过来：日程是记在某个人的某一天上的
    this.form.employeeId = this.route.query.employeeId || "";
    this.form.goalDate = this.route.query.date || today();
    if (this.isEdit) {
      this.findById();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  computed: {
    /** 带 id 进来就是编辑已有日程 */
    isEdit() {
      return !!this.route.query.id;
    },
  },
  methods: {
    /**
     * 编辑回显：日历行没有「按 id 查一条」的接口，按员工 + 日期把当天拉回来再挑出这一条。
     * 员工与日期由日历页带过来（与新建时是同一组参数）。
     */
    findById() {
      const id = this.route.query.id;
      if (!id || !this.form.employeeId || !this.form.goalDate) {
        return;
      }
      // queryDay 返回的是当天日历行数组，不能按 {calendarList: [...]} 取
      calendarApi.queryDay(this.form.employeeId, this.form.goalDate).then(list => {
        const row = (list || []).find(item => item.id === id);
        if (!row) {
          this.$message.error("没有找到这条日程");
          this.router.back();
          return;
        }
        this.form = {
          id: row.id,
          employeeId: row.employeeId || this.form.employeeId,
          calendarType: row.calendarType,
          partnerId: row.partnerId || "",
          title: row.title || "",
          goalDate: dateOnly(row.goalDate) || this.form.goalDate,
          remark: row.remark || "",
        };
        // 关联对象名称要单独查一次：日历行只存 partnerId，不存名称（规则8 不做快照）。
        // 客户与商机同表，merchant_remark_name 就是客户名 / 商机名，不用分情况
        if (!this.form.partnerId) {
          return;
        }
        commonDataApi.queryPartnerBaseInfoByIds([this.form.partnerId]).then(res => {
          const hit = (res || [])[0];
          if (hit) {
            this.partnerName = hit.merchantRemarkName || "";
          }
        });
      });
    },
    /** 从「拜访客户/商机客户」切到别的类型时清掉已选对象，避免带着无关数据提交 */
    onTypeChange(type) {
      const t = Number(type);
      if (t !== TYPE_VISIT && t !== TYPE_OPPORTUNITY) {
        this.form.partnerId = "";
        this.partnerName = "";
      }
    },
    /** 拜访客户用的客户选择：走通用往来单位接口（带数据权限） */
    queryCustomerPage(params) {
      return commonDataApi.queryPartnerList({...params, partnerType: PARTNER_TYPE.CUSTOMER});
    },
    /** 商机客户用的商机选择：走通用商机下拉接口（数据权限与商机列表页一致） */
    queryOpportunityPage(params) {
      return commonDataApi.queryOpportunityList(params);
    },
    cancel() {
      this.router.back();
    },
    saveData() {
      if (!this.form.calendarType) {
        this.$message.error("请选择日程类型");
        return;
      }
      const type = Number(this.form.calendarType);
      if (type === TYPE_VISIT && !this.form.partnerId) {
        this.$message.error("请选择要拜访的客户");
        return;
      }
      if (type === TYPE_OPPORTUNITY && !this.form.partnerId) {
        this.$message.error("请选择商机客户");
        return;
      }
      if (!this.form.title) {
        this.$message.error("请填写日程标题");
        return;
      }
      if (!this.form.goalDate) {
        this.$message.error("请选择日程日期");
        return;
      }
      const payload = JSON.parse(JSON.stringify(this.form));
      payload.calendarType = Number(payload.calendarType);
      // 成功提示由请求拦截器统一弹（saveSchedule 是 save 接口）
      calendarApi.saveSchedule(payload).then(() => this.router.back());
    }
  }
};
</script>

<style lang="scss" scoped>
.calendar-schedule{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 客户/商机选择（mobile-search-select）放在 data-input 行里：字号跟随整行。
  // 组件默认的 12px 是给列表页查询条件用的，放进表单行会比同页其它行明显小一圈。
  // ★ 选择器带上 .data-input：子组件 scoped 样式是 `.mobile-search-select .select-area[data-v-x]`，
  //   只写 :deep(.select-area) 与它层数相同、压不住，多垫一层才稳。
  :deep(.data-input .mobile-search-select .select-area){
    font-size: inherit;
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
